import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { IconButton, Stack, TextField, Typography, alpha } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { LayerItem } from "./types";
import { useLayerWeightInput } from "./useLayerWeightInput";

export type LayerEditorSize = "compact" | "comfortable";

interface Props {
  layer: LayerItem;
  color: string;
  onWeightChange: (id: string, weight: number) => void;
  onDelete: (id: string) => void;
  onSplit: (id: string) => void;
  weightsEditable: boolean;
  massScaleFactor: number;
  size: LayerEditorSize;
}

export const LayerBar = ({
  layer,
  color,
  onWeightChange,
  onDelete,
  onSplit,
  weightsEditable,
  massScaleFactor,
  size,
}: Props) => {
  const formatNumber = useFormatNumber();
  const displayWeight = layer.weight * massScaleFactor;
  const [editing, setEditing] = React.useState(false);
  const {
    draftWeight,
    setDraftWeight,
    commitWeight,
    resetWeight,
    unitLabel,
    decimalPlaces,
  } = useLayerWeightInput({
    weight: layer.weight,
    massScaleFactor,
    onWeightChange: (weight) => {
      onWeightChange(layer.id, weight);
    },
  });
  const isNew = layer.isNew ?? false;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleBlur = () => {
    setEditing(false);
    commitWeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Tab") {
      handleBlur();
    }
  };

  const startEditing = () => {
    resetWeight();
    setEditing(true);
  };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: 1,
        py: 0.5,
        bgcolor: alpha(color, 0.1),
        borderRadius: 1,
        border: isNew
          ? `1px dashed ${color}`
          : `1px solid ${alpha(color, 0.3)}`,
        borderLeft: `4px solid ${color}`,
        "&:hover .delete-btn": { opacity: 1 },
      }}
      onContextMenu={
        weightsEditable
          ? (e) => {
              e.preventDefault();
              onSplit(layer.id);
            }
          : undefined
      }
    >
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        sx={{ cursor: "grab" }}
      >
        <DragIndicatorRoundedIcon fontSize="small" />
      </IconButton>
      <Typography
        variant={size === "comfortable" ? "h5" : "body2"}
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...(size === "compact" && { fontWeight: "fontWeightSemiBold" }),
        }}
      >
        {layer.materialName}
      </Typography>
      {weightsEditable && editing ? (
        <TextField
          value={draftWeight}
          onChange={(e) => {
            setDraftWeight(e.target.value);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          sx={{ width: size === "comfortable" ? 120 : 90 }}
          slotProps={{
            htmlInput: { inputMode: "decimal" },
          }}
        />
      ) : (
        <Typography
          variant={size === "comfortable" ? "h5" : "body2"}
          data-testid="layer-weight"
          sx={{
            cursor: weightsEditable ? "pointer" : "default",
            minWidth: size === "comfortable" ? 90 : 60,
            textAlign: "right",
            ...(size === "compact" && { fontWeight: "fontWeightMedium" }),
          }}
          onClick={weightsEditable ? startEditing : undefined}
        >
          {formatNumber(decimalPlaces, true)(displayWeight)}
          {unitLabel}
        </Typography>
      )}
      {weightsEditable && (
        <IconButton
          size="small"
          className="delete-btn"
          onClick={() => {
            onDelete(layer.id);
          }}
          sx={{ opacity: 0, transition: "opacity 0.15s" }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
};
